import React, { useCallback, useEffect, useState } from "react";
import { useSiteContent } from "@/context/SiteContentContext";
import { notifyContentChanged } from "@/lib/contentSync";
import { fetchSitePayload, normalizePayload } from "@/admin/adminPayload";

function applyUrlToBranch(branch, pathStr, url) {
  const keys = pathStr.split(".").filter(Boolean);
  if (keys.length === 0) throw new Error("Path gol.");
  let cur = branch;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const nk = keys[i + 1];
    const existing = cur[k];
    if (existing != null && typeof existing !== "object") {
      throw new Error(`Conflict la „${k}”: există deja o valoare simplă.`);
    }
    if (existing == null) {
      cur[k] = /^\d+$/.test(nk) ? [] : {};
    }
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = url;
}

function insertUrlIntoPayload(parsed, targetId, pathStr, url) {
  const out = JSON.parse(JSON.stringify(parsed));
  let branch;
  if (targetId === "ro" || targetId === "en") {
    if (!out[targetId] || typeof out[targetId] !== "object" || Array.isArray(out[targetId])) {
      out[targetId] = {};
    }
    branch = out[targetId];
  } else if (targetId === "contactInfo") {
    if (!out.contactInfo || typeof out.contactInfo !== "object" || Array.isArray(out.contactInfo)) {
      out.contactInfo = {};
    }
    branch = out.contactInfo;
  } else if (targetId === "servicePages_ro") {
    if (!out.servicePages || typeof out.servicePages !== "object") out.servicePages = { ro: {}, en: {} };
    if (!out.servicePages.ro || typeof out.servicePages.ro !== "object" || Array.isArray(out.servicePages.ro)) {
      out.servicePages.ro = {};
    }
    branch = out.servicePages.ro;
  } else if (targetId === "servicePages_en") {
    if (!out.servicePages || typeof out.servicePages !== "object") out.servicePages = { ro: {}, en: {} };
    if (!out.servicePages.en || typeof out.servicePages.en !== "object" || Array.isArray(out.servicePages.en)) {
      out.servicePages.en = {};
    }
    branch = out.servicePages.en;
  } else {
    throw new Error("Țintă necunoscută.");
  }
  applyUrlToBranch(branch, pathStr, url);
  return out;
}

export default function AdminEditor() {
  const { reload } = useSiteContent();
  const [text, setText] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lastUploadUrl, setLastUploadUrl] = useState("");
  const [applyTarget, setApplyTarget] = useState("ro");
  const [applyPath, setApplyPath] = useState("portfolio.items.0.image");
  const [autoSaveAfterApply, setAutoSaveAfterApply] = useState(true);

  const loadPayload = useCallback(async () => {
    const payload = await fetchSitePayload();
    setText(JSON.stringify(payload, null, 2));
  }, []);

  useEffect(() => {
    loadPayload().catch(() => {});
  }, [loadPayload]);

  const savePayloadToServer = async (parsed) => {
    const payload = normalizePayload(parsed);
    setSaving(true);
    setMsg("");
    try {
      const r = await fetch("/api/admin/site-save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Salvare eșuată.");
        return false;
      }
      setMsg(
        "Salvat. Site-ul public și alte file deschise se actualizează rapid (semnal între file + reîncărcare la ~12s)."
      );
      reload();
      notifyContentChanged();
      return true;
    } catch {
      setMsg("Eroare de rețea.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    setMsg("");
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setMsg("JSON invalid. Verifică sintaxa.");
      return;
    }
    await savePayloadToServer(parsed);
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setMsg("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/admin/upload.php", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Upload eșuat.");
        return;
      }
      setLastUploadUrl(j.url || "");
      setMsg(`Imagine încărcată. Poți aplica URL-ul în JSON mai jos: ${j.url}`);
    } catch {
      setMsg("Eroare la upload.");
    } finally {
      setUploading(false);
    }
  };

  const onApplyUploadedUrl = async () => {
    if (!lastUploadUrl) {
      setMsg("Încarcă mai întâi o imagine.");
      return;
    }
    const pathTrim = applyPath.trim();
    if (!pathTrim) {
      setMsg("Completează path-ul (ex: portfolio.items.0.image).");
      return;
    }
    setMsg("");
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setMsg("JSON invalid. Verifică sintaxa.");
      return;
    }
    try {
      const next = insertUrlIntoPayload(parsed, applyTarget, pathTrim, lastUploadUrl);
      setText(JSON.stringify(next, null, 2));
      if (autoSaveAfterApply) {
        await savePayloadToServer(next);
      } else {
        setMsg("URL aplicat în editor. Apasă „Salvează în baza de date” când ești gata.");
      }
    } catch (err) {
      setMsg(err?.message || "Nu s-a putut aplica URL-ul.");
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 text-[#f5f0ee]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Conținut site (override JSON)</h1>
          <p className="mt-1 max-w-2xl text-sm text-white/50">
            Editezi doar diferențele față de textele implicite din cod. Chei ca în{" "}
            <code className="text-white/70">translations.js</code> și{" "}
            <code className="text-white/70">services.js</code>. Poți încărca imagini pe server (mai jos) și le poți lega
            de JSON. După salvare, site-ul public se actualizează automat (polling ~12s + semnal între file). Pentru
            editare pe câmpuri, folosește tab-ul <strong className="text-white/70">Personalizare</strong>.
          </p>
        </div>
        <button type="button" onClick={loadPayload} className="rounded-full border border-white/20 px-4 py-2 text-sm">
          Reîncarcă din DB
        </button>
      </div>

        <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3 text-sm">
          <p className="font-medium text-white/80">Încărcare imagini (JPEG, PNG, WebP, GIF · max 8 MB)</p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="cursor-pointer rounded-full border border-white/20 px-4 py-2 hover:bg-white/5">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="sr-only"
                onChange={onUploadFile}
                disabled={uploading || saving}
              />
              {uploading ? "Se încarcă…" : "Alege fișier"}
            </label>
            {lastUploadUrl ? (
              <code className="max-w-full break-all text-xs text-[#fbd9e5]/90">{lastUploadUrl}</code>
            ) : (
              <span className="text-white/40 text-xs">Fișierele merg în /api/uploads/ pe server.</span>
            )}
          </div>
          {lastUploadUrl ? (
            <div className="flex flex-col gap-3 border-t border-white/10 pt-3 sm:flex-row sm:flex-wrap sm:items-end">
              <label className="flex min-w-[140px] flex-col gap-1 text-xs text-white/50">
                Țintă
                <select
                  value={applyTarget}
                  onChange={(e) => setApplyTarget(e.target.value)}
                  className="rounded-lg border border-white/15 bg-black/40 px-2 py-2 text-[#e8e0dc]"
                >
                  <option value="ro">ro</option>
                  <option value="en">en</option>
                  <option value="contactInfo">contactInfo</option>
                  <option value="servicePages_ro">servicePages.ro</option>
                  <option value="servicePages_en">servicePages.en</option>
                </select>
              </label>
              <label className="flex min-w-[min(100%,220px)] flex-1 flex-col gap-1 text-xs text-white/50">
                Path (ex: portfolio.items.0.image)
                <input
                  value={applyPath}
                  onChange={(e) => setApplyPath(e.target.value)}
                  className="rounded-lg border border-white/15 bg-black/40 px-2 py-2 font-mono text-[12px] text-[#e8e0dc]"
                />
              </label>
              <label className="flex cursor-pointer items-center gap-2 pb-2 text-xs text-white/60">
                <input
                  type="checkbox"
                  checked={autoSaveAfterApply}
                  onChange={(e) => setAutoSaveAfterApply(e.target.checked)}
                />
                Salvează imediat în DB
              </label>
              <button
                type="button"
                onClick={onApplyUploadedUrl}
                disabled={saving}
                className="rounded-full bg-white/15 px-5 py-2 text-xs font-semibold text-white/90 hover:bg-white/20 disabled:opacity-50"
              >
                Aplică URL în JSON
              </button>
            </div>
          ) : null}
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          className="h-[min(70vh,720px)] w-full rounded-xl border border-white/10 bg-black/40 p-4 font-mono text-[13px] leading-relaxed text-[#e8e0dc] outline-none focus:ring-2 focus:ring-[#fbd9e5]/30"
        />
        {msg ? <p className="text-sm text-[#fbd9e5]/90">{msg}</p> : null}
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded-full bg-[#fbd9e5] px-8 py-3 text-sm font-semibold text-[#3e2723] disabled:opacity-50"
        >
          {saving ? "Se salvează…" : "Salvează în baza de date"}
        </button>
    </div>
  );
}
